import React from 'react';
import { Navigate } from 'react-router-dom';
import { ShieldAlert } from 'lucide-react';

/**
 * ProtectedRoute Component
 * Implements Role-Based Access Control (RBAC) for routes
 * 
 * @param {React.Component} children - The component to render if authorized
 * @param {Array<string>} allowedRoles - List of roles that can access this route
 * @param {Array<string>} requiredPermissions - List of permissions required (optional, for fine-grained control)
 * @param {string} redirectTo - Path to redirect if unauthorized (default: /dashboard)
 */
const ProtectedRoute = ({ 
    children, 
    allowedRoles = [], 
    requiredPermissions = [],
    redirectTo = '/' 
}) => {
    // Get current user from localStorage
    const userStr = localStorage.getItem('user');
    
    if (!userStr) {
        // User not logged in, redirect to persona selection (root)
        return <Navigate to="/" replace />;
    }

    const user = JSON.parse(userStr);
    const userRole = user?.role || user?.user_type || '';
    const userPermissions = user?.permissions || [];

    // Check if user's role is in allowedRoles
    const hasRoleAccess = allowedRoles.length === 0 || allowedRoles.some(role => 
        userRole.toLowerCase().includes(role.toLowerCase())
    );

    // Check if user has required permissions (if specified)
    const hasPermissionAccess = requiredPermissions.length === 0 || 
        requiredPermissions.every(permission => 
            userPermissions.includes(permission)
        );

    // If user has access, render children
    if (hasRoleAccess && hasPermissionAccess) {
        return children;
    }

    // User doesn't have access - show unauthorized page
    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
            <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
                <div className="flex justify-center mb-4">
                    <ShieldAlert className="h-16 w-16 text-red-500" />
                </div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
                <p className="text-gray-600 mb-6">
                    You don't have permission to access this resource.
                </p>
                <div className="space-y-2 mb-6">
                    <p className="text-sm text-gray-500">
                        <span className="font-medium">Your Role:</span> {userRole}
                    </p>
                    <p className="text-sm text-gray-500">
                        <span className="font-medium">Required Roles:</span> {allowedRoles.join(', ') || 'Any'}
                    </p>
                </div>
                <button
                    onClick={() => window.location.href = redirectTo}
                    className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                    Go to Home
                </button>
            </div>
        </div>
    );
};

export default ProtectedRoute;
